// src/mapPois.js
import PopupMap from "./PopupMap.svelte";

function getBlackOrWhite(color) {
  return parseInt(color.replace("#", ""), 16) > 0xffffff / 2
    ? "#000000"
    : "#ffffff";
}

function getPopup(poi, scenes) {
  if (!poi) return;
  const { title, description } = poi;
  let { sceneIds, location } = poi;
  // Parse JSON strings
  if (typeof sceneIds === "string") sceneIds = JSON.parse(sceneIds);
  if (typeof location === "string") location = JSON.parse(location);

  // convert the scene ids into linked scenes
  const linkedscenes = sceneIds?.map((sceneId) => {
    const scene = scenes?.find((scene) => scene.id === sceneId);
    return {
      id: sceneId,
      name: scene?.name,
      title: scene?.title,
    };
  });

  return {
    title,
    content: description,
    linkedscenes,
    directions: location,
  };
}

// get a darker or lighter color for hover effect (depending on darkness of color)
function getHoverColor(color) {
  const colorInt = parseInt(color.replace("#", ""), 16);
  const isDark = colorInt > 0xffffff / 2;
  const factor = isDark ? 0.9 : 1.1;
  const newColorInt = Math.round(colorInt * factor);
  return "#" + newColorInt.toString(16);
}

export function removePois({ map }) {
  console.log("removePois");
  // Remove event listeners if they exist
  if (map._poiListeners) {
    Object.entries(map._poiListeners.clusters).forEach(([event, handler]) => {
      map.off(event, "clusters", handler);
    });
    Object.entries(map._poiListeners.unclusteredPoint).forEach(([event, handler]) => {
      map.off(event, "unclustered-point", handler);
    });
    delete map._poiListeners;
  }

  // Remove all POI-related layers
  const poiLayers = ['clusters', 'cluster-count', 'unclustered-point'];
  poiLayers.forEach(layer => {
    if (map.getLayer(layer)) {
      map.removeLayer(layer);
    }
  });

  // Remove the POI source
  if (map.getSource('pois')) {
    map.removeSource('pois');
  }
}

export function addPOIs({ map, pois, bgColor, textColor, setTooltip, scenes }) {
  // filter out hidden pois
  pois = pois.filter((poi) => !poi.hidden);
  // setup colors
  bgColor = bgColor || "#000000";
  textColor = textColor || getBlackOrWhite(bgColor);
  const strokeColor = textColor;
  // just use the same color as the points for now
  const clusterColor = bgColor;
  // determine if cluster text color should be white or black based on darkness of cluster color
  const clusterTextColor = textColor;

  const geojsonSource = {
    type: "FeatureCollection",
    features: pois.map((poi) => ({
      type: "Feature",
      properties: poi,
      geometry: {
        type: "Point",
        coordinates: poi.location,
      },
    })),
  };

  map.addSource("pois", {
    type: "geojson",
    data: geojsonSource,
    cluster: true,
    clusterRadius: 50,
    clusterMaxZoom: 14,
  });

  map.addLayer({
    id: "clusters",
    type: "circle",
    source: "pois",
    filter: ["has", "point_count"],
    paint: {
      "circle-color": clusterColor,
      "circle-radius": 24,
      "circle-stroke-width": 2,
      "circle-stroke-color": clusterTextColor,
    },
  });

  map.addLayer({
    id: "cluster-count",
    type: "symbol",
    source: "pois",
    filter: ["has", "point_count"],
    layout: {
      "text-field": "{point_count_abbreviated}",
      "text-font": ["Arial Unicode MS Bold", "DIN Offc Pro Bold"],
      "text-size": 12,
    },
    paint: {
      "text-color": clusterTextColor,
    },
  });

  map.addLayer({
    id: "unclustered-point",
    type: "circle",
    source: "pois",
    filter: ["!", ["has", "point_count"]],
    paint: {
      "circle-color": bgColor,
      "circle-radius": 10,
      "circle-stroke-width": 2,
      "circle-stroke-color": strokeColor,
    },
  });

  // Store event listeners for cleanup
  const listeners = {
    clusters: {
      click: (e) => {
        const features = map.queryRenderedFeatures(e.point, {
          layers: ["clusters"],
        });
        const clusterId = features[0].properties.cluster_id;
        const clusterSource = map.getSource("pois");

        clusterSource.getClusterLeaves(
          clusterId,
          Infinity,
          0,
          (error, points) => {
            if (error) return;
            const coordinates = points.map((point) => point.geometry.coordinates);
            const bounds = coordinates.reduce(
              (bounds, coord) => bounds.extend(coord),
              new mapboxgl.LngLatBounds(coordinates[0], coordinates[0]),
            );
            map.fitBounds(bounds, {
              padding: 100,
              maxZoom: 17,
            });
          },
        );
      },
      mouseenter: () => {
        map.getCanvas().style.cursor = "pointer";
      },
      mousemove: (e) => {
        const features = map.queryRenderedFeatures(e.point, {
          layers: ["clusters"],
        });
        if (features.length) {
          map.getCanvas().style.cursor = "pointer";
        } else {
          map.getCanvas().style.cursor = "";
        }
      },
      mouseleave: () => {
        map.getCanvas().style.cursor = "";
      }
    },
    unclusteredPoint: {
      click: (e) => {
        console.log("poi click", e.features[0].properties);
        const popup = getPopup(e.features?.[0]?.properties, scenes);
        const popupMap = new PopupMap({
          target: document.body,
          props: popup,
        });
      },
      mousemove: (e) => {
        const x = e.point.x;
        const y = e.point.y;
        const text = e.features[0]?.properties?.title;
        if (text) setTooltip({ x, y, text });

        const popup = getPopup(e.features?.[0]?.properties, scenes);
        if (popup) {
          map.getCanvas().style.cursor = "pointer";
        }
      },
      mouseleave: () => {
        map.getCanvas().style.cursor = "";
        setTooltip(null);
      }
    }
  };

  // Add event listeners
  Object.entries(listeners.clusters).forEach(([event, handler]) => {
    map.on(event, "clusters", handler);
  });

  Object.entries(listeners.unclusteredPoint).forEach(([event, handler]) => {
    map.on(event, "unclustered-point", handler);
  });

  // Store listeners on the map object for cleanup
  map._poiListeners = listeners;
}

export function addNumericPOIs({ map, pois, bgColor, textColor, setTooltip, scenes }) {
  // filter out hidden pois
  pois = pois.filter((poi) => !poi.hidden);
  // sort pois by order
  pois = pois.sort((a, b) => a.order - b.order);
  // setup colors
  bgColor = bgColor || "#000000";
  textColor = textColor || getBlackOrWhite(bgColor);
  const strokeColor = textColor;

  const geojsonSource = {
    type: "FeatureCollection",
    features: pois.map((poi, index) => ({
      type: "Feature",
      properties: {
        ...poi,
        number: index + 1, // Add numeric property
      },
      geometry: {
        type: "Point",
        coordinates: poi.location,
      },
    })),
  };

  map.addSource("numeric-pois", {
    type: "geojson",
    data: geojsonSource,
  });

  // Add circle background layer
  map.addLayer({
    id: "numeric-points",
    type: "circle",
    source: "numeric-pois",
    paint: {
      "circle-color": bgColor,
      "circle-radius": 12,
      "circle-stroke-width": 2,
      "circle-stroke-color": strokeColor,
    },
  });

  // Add number labels
  map.addLayer({
    id: "numeric-labels",
    type: "symbol",
    source: "numeric-pois",
    layout: {
      "text-field": "{number}",
      "text-font": ["Arial Unicode MS Bold"],
      "text-size": 12,
      "text-allow-overlap": true,
    },
    paint: {
      "text-color": textColor,
    },
  });

  // Store event listeners for cleanup
  const listeners = {
    numericPoints: {
      click: (e) => {
        console.log("numeric poi click", e.features[0].properties);
        const popup = getPopup(e.features?.[0]?.properties, scenes);
        const popupMap = new PopupMap({
          target: document.body,
          props: popup,
        });
      },
      mousemove: (e) => {
        const x = e.point.x;
        const y = e.point.y;
        const text = e.features[0]?.properties?.title;
        if (text) setTooltip({ x, y, text });

        const popup = getPopup(e.features?.[0]?.properties, scenes);
        if (popup) {
          map.getCanvas().style.cursor = "pointer";
        }
      },
      mouseleave: () => {
        map.getCanvas().style.cursor = "";
        setTooltip(null);
      }
    }
  };

  // Add event listeners
  Object.entries(listeners.numericPoints).forEach(([event, handler]) => {
    map.on(event, "numeric-points", handler);
  });

  // Store listeners on the map object for cleanup
  map._numericPoiListeners = listeners;
}

export function removeNumericPois({ map }) {
  console.log("removeNumericPois");
  // Remove event listeners if they exist
  if (map._numericPoiListeners) {
    Object.entries(map._numericPoiListeners.numericPoints).forEach(([event, handler]) => {
      map.off(event, "numeric-points", handler);
    });
    delete map._numericPoiListeners;
  }

  // Remove all numeric POI layers
  const numericLayers = ['numeric-points', 'numeric-labels'];
  numericLayers.forEach(layer => {
    if (map.getLayer(layer)) {
      map.removeLayer(layer);
    }
  });

  // Remove the numeric POI source
  if (map.getSource('numeric-pois')) {
    map.removeSource('numeric-pois');
  }
}
